/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { CheckIcon, CircleAlertIcon, CopyIcon, Trash2Icon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Spinner } from '@/components/ui'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export type ConfirmDialogVariant = 'delete' | 'clone' | 'basic'

export type ConfirmDialogProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
  variant?: ConfirmDialogVariant
  icon?: ReactNode
  beforeControls?: ReactNode
  additionalControls?: ReactNode
  title?: string
  text?: string
  confirmationText?: string
  confirmationPrompt?: string
  confirmationPlaceholder?: string
  cancelText?: string
  confirmText?: string
  loading?: boolean
  confirmDisabled?: boolean
  onConfirm: () => void
}

export function ConfirmDialog({
  open,
  onOpenChange,
  variant = 'basic',
  icon,
  beforeControls,
  additionalControls,
  title,
  text,
  confirmationText,
  confirmationPrompt,
  confirmationPlaceholder,
  cancelText,
  confirmText,
  loading = false,
  confirmDisabled = false,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  const resolvedTitle = title ?? t('Delete Confirmation')
  const resolvedText = text ?? t('Are you sure you want to delete this item?')
  const resolvedConfirmationPrompt = confirmationPrompt ?? t('Please type the confirmation text to continue')
  const resolvedConfirmationPlaceholder = confirmationPlaceholder ?? t('Type the confirmation text here')
  const resolvedCancelText = cancelText ?? t('Cancel')
  const resolvedConfirmText = confirmText ?? t('Delete')
  const [typedText, setTypedText] = useState('')
  const [copied, setCopied] = useState(false)
  const reportedCloseRef = useRef(false)
  const requiresTextConfirmation = !!confirmationText
  const isConfirmMatch = !requiresTextConfirmation || typedText === confirmationText
  const mediaStylesByVariant: Record<ConfirmDialogVariant, string> = {
    delete: 'bg-destructive/10 text-destructive dark:bg-destructive/80 dark:text-destructive-foreground/80',
    clone: 'bg-primary/10 text-primary dark:bg-primary/80 dark:text-primary-foreground/80',
    basic: 'bg-muted text-muted-foreground',
  }
  const defaultIconByVariant: Record<ConfirmDialogVariant, ReactNode> = {
    delete: <Trash2Icon />,
    clone: <CopyIcon />,
    basic: <CircleAlertIcon />,
  }

  useEffect(() => {
    if (open) {
      reportedCloseRef.current = false
      onOpenChange?.({ open: true })
    } else {
      if (!reportedCloseRef.current) {
        onOpenChange?.({ open: false })
      }
      reportedCloseRef.current = false
      setTypedText('')
      setCopied(false)
    }
  }, [open])

  const handleCopyConfirmationText = async () => {
    if (!confirmationText) return

    await navigator.clipboard.writeText(confirmationText)
    setCopied(true)
    window.setTimeout(() => {
      setCopied(false)
    }, 1500)
  }

  return (
    <AlertDialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          reportedCloseRef.current = true
          onOpenChange?.({ open: false })
        }
      }}
      open={open}
    >
      <AlertDialogContent initialFocus={false} size="default">
        <AlertDialogHeader>
          <AlertDialogMedia className={mediaStylesByVariant[variant]}>
            {icon ?? defaultIconByVariant[variant]}
          </AlertDialogMedia>
          <AlertDialogTitle>{resolvedTitle}</AlertDialogTitle>
          <AlertDialogDescription>{resolvedText}</AlertDialogDescription>
        </AlertDialogHeader>

        {beforeControls}

        {requiresTextConfirmation && (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="font-semibold text-base">{resolvedConfirmationPrompt}</p>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-base text-destructive">{confirmationText}</p>
                <Button
                  aria-label={t('Copy confirmation text')}
                  onClick={handleCopyConfirmationText}
                  type="button"
                  variant="outline"
                >
                  {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
                </Button>
              </div>
              <Input
                autoComplete="off"
                className={cn('w-full')}
                onChange={(event) => setTypedText(event.target.value)}
                placeholder={resolvedConfirmationPlaceholder}
                value={typedText}
              />
            </div>
          </div>
        )}

        {additionalControls}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading} type="button">
            {resolvedCancelText}
          </AlertDialogCancel>
          <AlertDialogAction disabled={!isConfirmMatch || loading || confirmDisabled} onClick={onConfirm} type="button">
            {loading ? <Spinner /> : resolvedConfirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
